import { EMBEDDING_PRICE_KEY, TOKEN_PRICE_KEYS, TOKEN_PRICE_MAX, type TokenPriceKey, type TokenPrices } from '@rp/contracts'
import { useState } from 'react'
import { isApiError } from '../../api/client'
import { Button } from '../../design'
import { LOCALE } from '../../lib/time'
import { USAGE_COPY } from './copy'
import { useUpdateTokenPrices } from './queries'

const DEV_ONLY_PROVIDER = 'fake'
const PRICE_PATTERN = /^\d+(\.\d+)?$/
const PRICE_ERROR_ID = 'usage-price-error'
const PRICE_HINT_ID_PREFIX = 'usage-price-hint-'
const PRICE_INPUT_ID_PREFIX = 'usage-price-'
const PRICED_KEYS = TOKEN_PRICE_KEYS.filter((key) => key !== DEV_ONLY_PROVIDER)
const REFERENCE_PRICES: Partial<Record<TokenPriceKey, number>> = { vertex_gemini: 0.74, [EMBEDDING_PRICE_KEY]: 0.15 }
const INVALID_PRICE = USAGE_COPY.priceInvalid(new Intl.NumberFormat(LOCALE).format(TOKEN_PRICE_MAX))

const labelOf = (key: TokenPriceKey): string => (key === EMBEDDING_PRICE_KEY ? USAGE_COPY.providerless : key)

type ParsedPrice = { ok: true; value: number | undefined } | { ok: false }

const parsePrice = (text: string): ParsedPrice => {
  const trimmed = text.trim()
  if (trimmed === '') return { ok: true, value: undefined }
  const value = Number(trimmed)
  return PRICE_PATTERN.test(trimmed) && value <= TOKEN_PRICE_MAX ? { ok: true, value } : { ok: false }
}

const invalidKeysOf = (texts: Record<TokenPriceKey, string>): TokenPriceKey[] =>
  PRICED_KEYS.filter((key) => !parsePrice(texts[key]).ok)

const pricesFrom = (texts: Record<TokenPriceKey, string>): TokenPrices =>
  Object.fromEntries(
    PRICED_KEYS.flatMap((key) => {
      const price = parsePrice(texts[key])
      return price.ok && price.value !== undefined ? [[key, price.value]] : []
    }),
  )

const textsFrom = (prices: TokenPrices): Record<TokenPriceKey, string> =>
  Object.fromEntries(TOKEN_PRICE_KEYS.map((key) => [key, prices[key] === undefined ? '' : String(prices[key])])) as Record<
    TokenPriceKey,
    string
  >

interface PriceFormProps {
  prices: TokenPrices
  reload: () => Promise<TokenPrices | null>
}

const isConflict = (error: unknown): boolean => isApiError(error) && error.code === 'CONFLICT'

type ReloadState = 'idle' | 'reloading' | 'failed'

const CONFLICT_COPY: Record<ReloadState, string> = {
  idle: USAGE_COPY.pricesConflict,
  reloading: USAGE_COPY.pricesConflictReloading,
  failed: USAGE_COPY.pricesConflictReloadFailed,
}

const failureOf = (error: unknown, reload: ReloadState): string | null => {
  if (!isConflict(error)) return isApiError(error) ? error.message : null
  return CONFLICT_COPY[reload]
}

const describedBy = (ids: readonly (string | null)[]): string | undefined => {
  const present = ids.filter((id): id is string => id !== null)
  return present.length === 0 ? undefined : present.join(' ')
}

export const PriceForm = ({ prices, reload }: PriceFormProps) => {
  const update = useUpdateTokenPrices()
  const [baseline, setBaseline] = useState(prices)
  const [texts, setTexts] = useState(() => textsFrom(prices))
  const [invalidKeys, setInvalidKeys] = useState<TokenPriceKey[]>([])
  const [reloadState, setReloadState] = useState<ReloadState>('idle')
  const reloading = reloadState === 'reloading'
  const locked = update.isPending || reloading
  const failure = failureOf(update.error, reloadState)
  const invalid = invalidKeys.length > 0

  const edit = (key: TokenPriceKey, value: string) => {
    setTexts((current) => ({ ...current, [key]: value }))
    update.reset()
  }

  const loadNewer = async () => {
    setReloadState('reloading')
    const newer = await reload()
    setReloadState(newer === null ? 'failed' : 'idle')
    if (newer === null) return
    setBaseline(newer)
    setTexts(textsFrom(newer))
  }

  const save = () => {
    const nextInvalid = invalidKeysOf(texts)
    setInvalidKeys(nextInvalid)
    setReloadState('idle')
    if (nextInvalid.length > 0) return
    update.mutate(
      { tokenPrices: pricesFrom(texts), expectedTokenPrices: baseline },
      {
        onSuccess: (saved) => setBaseline(saved.tokenPrices),
        onError: (error) => (isConflict(error) ? loadNewer() : undefined),
      },
    )
  }

  return (
    <div className="usage-prices">
      <h3>{USAGE_COPY.pricesHeading}</h3>
      <div className="usage-price-grid">
        {PRICED_KEYS.map((key) => {
          const reference = REFERENCE_PRICES[key]
          const keyInvalid = invalidKeys.includes(key)
          const hintId = reference === undefined ? null : `${PRICE_HINT_ID_PREFIX}${key}`
          const inputId = `${PRICE_INPUT_ID_PREFIX}${key}`
          return (
            <div key={key} className="field usage-price-row">
              <label htmlFor={inputId}>{labelOf(key)}</label>
              <input
                id={inputId}
                className="usage-price-input"
                inputMode="decimal"
                value={texts[key]}
                disabled={locked}
                aria-invalid={keyInvalid}
                aria-describedby={describedBy([hintId, keyInvalid ? PRICE_ERROR_ID : null])}
                onChange={(event) => edit(key, event.target.value)}
              />
              {hintId === null || reference === undefined ? null : (
                <span className="field-hint" id={hintId}>
                  {USAGE_COPY.pricesReference(reference)}
                </span>
              )}
            </div>
          )
        })}
      </div>
      <p className="usage-updated">{USAGE_COPY.pricesReferenceNote}</p>
      {invalid ? (
        <p id={PRICE_ERROR_ID} className="text-tone tone-danger" role="alert">
          {INVALID_PRICE}
        </p>
      ) : null}
      {failure === null ? null : (
        <p className="text-tone tone-danger" role="alert">
          {failure}
        </p>
      )}
      <div className="usage-budget-actions">
        <Button variant="primary" pending={locked} pendingLabel={reloading ? USAGE_COPY.reloadingPrices : USAGE_COPY.savingPrices} onClick={save}>
          {USAGE_COPY.savePrices}
        </Button>
        {update.isSuccess ? (
          <span className="text-tone tone-success" role="status">
            {USAGE_COPY.pricesSaved}
          </span>
        ) : null}
      </div>
    </div>
  )
}
